import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DropAnimation,
} from "@dnd-kit/core"
import {
  Plus,
  Loader2,
  User,
  DollarSign,
  Search,
  Filter,
  X,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  dealsApi,
  usersApi,
  ApiError,
  type DealPipelineCard,
  type PipelineStage,
  type FinanceType,
  type Priority,
} from "@/lib/api"
import { LenderLogo } from "@/components/LenderLogo"
import { SettledEnrolDialog } from "@/components/SettledEnrolDialog"
import { SettleCompleteDialog } from "@/components/SettleCompleteDialog"
import { formatCurrency } from "@shared/utils/currency"
import { timeAgo } from "@shared/utils/date"
import { cn } from "@/lib/utils"

const STAGE_LABEL: Record<PipelineStage, string> = {
  new_lead: "New Lead",
  day_one: "Day 1",
  day_two: "Day 2",
  day_three: "Day 3",
  general_followup: "General Follow-up",
  awaiting_docs: "Awaiting Docs",
  docs_received: "Docs Received",
  approved: "Approved",
  settled: "Settled",
  closed: "Closed",
}
const STAGE_COLOR: Record<PipelineStage, string> = {
  new_lead: "bg-blue-500",
  day_one: "bg-blue-400",
  day_two: "bg-indigo-400",
  day_three: "bg-indigo-500",
  general_followup: "bg-amber-500",
  awaiting_docs: "bg-purple-500",
  docs_received: "bg-cyan-500",
  approved: "bg-emerald-500",
  settled: "bg-emerald-600",
  closed: "bg-red-500",
}

// ------------------------------------------------------------
// Filters — mirrors the ContactsListPage pattern so brokers get the
// same mental model everywhere. Applied client-side against the
// already-fetched pipeline data (fast, no extra API calls).
// ------------------------------------------------------------
type PipelineFilters = {
  q: string
  ownerId: string
  financeType: FinanceType | ""
  priority: Priority | ""
}

const EMPTY_FILTERS: PipelineFilters = {
  q: "",
  ownerId: "",
  financeType: "",
  priority: "",
}

// localStorage key for sticky pipeline filters. Bumped when the shape
// changes so old cached blobs don't crash the reader.
const PIPELINE_FILTERS_LS_KEY = "rivercity.pipeline.filters.v1"

function loadStickyFilters(): PipelineFilters {
  if (typeof window === "undefined") return EMPTY_FILTERS
  try {
    const raw = window.localStorage.getItem(PIPELINE_FILTERS_LS_KEY)
    if (!raw) return EMPTY_FILTERS
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return EMPTY_FILTERS
    // Search query is transient — don't restore it, brokers rarely want
    // a stale text search to reappear next session.
    return {
      q: "",
      ownerId: typeof parsed.ownerId === "string" ? parsed.ownerId : "",
      financeType:
        typeof parsed.financeType === "string"
          ? (parsed.financeType as FinanceType | "")
          : "",
      priority:
        typeof parsed.priority === "string"
          ? (parsed.priority as Priority | "")
          : "",
    }
  } catch {
    return EMPTY_FILTERS
  }
}

function saveStickyFilters(f: PipelineFilters): void {
  if (typeof window === "undefined") return
  try {
    // Only persist the sticky bits — leave `q` out.
    const toSave = {
      ownerId: f.ownerId,
      financeType: f.financeType,
      priority: f.priority,
    }
    window.localStorage.setItem(
      PIPELINE_FILTERS_LS_KEY,
      JSON.stringify(toSave)
    )
  } catch {
    // localStorage full / disabled — not fatal, filter just won't stick.
  }
}

export default function PipelinePage() {
  const qc = useQueryClient()
  const [activeCard, setActiveCard] = useState<DealPipelineCard | null>(null)

  // Filters are stored in the URL so refresh + browser-back keep the
  // broker's selection (Andy 2026-08-31: pipeline dropdowns kept
  // resetting to "All brokers" on refresh). URL is the source of
  // truth; the same setter is used by the FilterBar sub-component.
  //
  // On top of that, ownerId/financeType/priority are also mirrored to
  // localStorage so a fresh navigation to /pipeline (from sidebar, new
  // tab, next login) restores the broker's last selection. Search text
  // is intentionally NOT persisted — stale searches are annoying.
  const [searchParams, setSearchParams] = useSearchParams()

  // Hydrate from localStorage on first mount, but only if the URL is
  // completely empty of pipeline filter params. If the URL already has
  // filters (e.g. shared link, back button), the URL wins.
  useEffect(() => {
    const urlHasFilters =
      searchParams.has("ownerId") ||
      searchParams.has("financeType") ||
      searchParams.has("priority") ||
      searchParams.has("q")
    if (urlHasFilters) return
    const sticky = loadStickyFilters()
    if (!sticky.ownerId && !sticky.financeType && !sticky.priority) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (sticky.ownerId) next.set("ownerId", sticky.ownerId)
        if (sticky.financeType) next.set("financeType", sticky.financeType)
        if (sticky.priority) next.set("priority", sticky.priority)
        return next
      },
      { replace: true }
    )
    // Only run on mount — subsequent URL changes shouldn't re-hydrate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const filters: PipelineFilters = useMemo(
    () => ({
      q: searchParams.get("q") ?? "",
      ownerId: searchParams.get("ownerId") ?? "",
      financeType: (searchParams.get("financeType") as FinanceType | "") || "",
      priority: (searchParams.get("priority") as Priority | "") || "",
    }),
    [searchParams]
  )

  // Mirror the current filter set to localStorage so it sticks across
  // sessions and fresh navigations. Runs whenever filters change.
  useEffect(() => {
    saveStickyFilters(filters)
  }, [filters])
  const setFilters = useCallback(
    (next: PipelineFilters | ((prev: PipelineFilters) => PipelineFilters)) => {
      setSearchParams((prev) => {
        // Support both direct value and updater-function forms so
        // existing setFilters(EMPTY_FILTERS) and setFilters(f => ...)
        // call sites keep working.
        const current: PipelineFilters = {
          q: prev.get("q") ?? "",
          ownerId: prev.get("ownerId") ?? "",
          financeType: (prev.get("financeType") as FinanceType | "") || "",
          priority: (prev.get("priority") as Priority | "") || "",
        }
        const resolved =
          typeof next === "function"
            ? (next as (p: PipelineFilters) => PipelineFilters)(current)
            : next
        const params = new URLSearchParams(prev)
        const upsert = (key: keyof PipelineFilters) => {
          const v = resolved[key]
          if (v) params.set(key, String(v))
          else params.delete(key)
        }
        upsert("q")
        upsert("ownerId")
        upsert("financeType")
        upsert("priority")
        return params
      })
    },
    [setSearchParams]
  )
  // Surface a transient banner when a drag-and-drop move fails so the broker
  // sees WHY the card snapped back. (L-9)
  const [moveError, setMoveError] = useState<string | null>(null)

  const pipeline = useQuery({
    queryKey: ["deals-pipeline"],
    queryFn: () => dealsApi.pipeline(),
  })

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  const [settledDialog, setSettledDialog] = useState<{
    dealId: string
    contactId: string
    automationIds: string[]
  } | null>(null)

  // Opened when a broker drops on Settled but the API rejects with
  // fieldErrors. Lets them fill the missing fields inline instead of
  // navigating to the deal detail page.
  const [settleCompleteDialog, setSettleCompleteDialog] = useState<{
    dealId: string
    contactId: string | null
    missingFields: string[]
  } | null>(null)

  const move = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: PipelineStage }) => dealsApi.move(id, stage),
    onSuccess: (res, vars) => {
      // If the deal just transitioned INTO settled, open the enrol dialog.
      // We need the deal's contactId — look it up from the pipeline board
      // cache we already have loaded.
      if (
        res.transitionedIntoSettled &&
        res.settledEnrolAutomationIds &&
        res.settledEnrolAutomationIds.length > 0
      ) {
        const board = qc.getQueryData<
          Awaited<ReturnType<typeof dealsApi.pipeline>>
        >(["deals-pipeline"])
        let card: DealPipelineCard | undefined
        if (board) {
          for (const s of Object.keys(board.board) as PipelineStage[]) {
            const found = board.board[s].find((c) => c.id === vars.id)
            if (found) {
              card = found
              break
            }
          }
        }
        if (card?.contactId) {
          setSettledDialog({
            dealId: vars.id,
            contactId: card.contactId,
            automationIds: res.settledEnrolAutomationIds,
          })
        }
      }
    },
    onMutate: async ({ id, stage }) => {
      // Optimistic update
      await qc.cancelQueries({ queryKey: ["deals-pipeline"] })
      const previous = qc.getQueryData<Awaited<ReturnType<typeof dealsApi.pipeline>>>([
        "deals-pipeline",
      ])
      if (previous) {
        const newBoard = { ...previous.board }
        let card: DealPipelineCard | undefined
        for (const s of Object.keys(newBoard) as PipelineStage[]) {
          const idx = newBoard[s].findIndex((c) => c.id === id)
          if (idx >= 0) {
            card = newBoard[s][idx]
            newBoard[s] = [...newBoard[s]]
            newBoard[s].splice(idx, 1)
            break
          }
        }
        if (card) {
          const updatedCard = { ...card, pipelineStage: stage }
          newBoard[stage] = [updatedCard, ...(newBoard[stage] ?? [])]
        }
        qc.setQueryData(["deals-pipeline"], { ...previous, board: newBoard })
      }
      return { previous }
    },
    onError: (err, vars, ctx) => {
      // Always revert the optimistic drop first so the card is back where
      // it started — the modal below (or the toast fallback) is the only
      // thing that stays.
      if (ctx?.previous) qc.setQueryData(["deals-pipeline"], ctx.previous)

      // Settle-block special case: API returns 400 with structured
      // fieldErrors listing what's missing. Pop the completion modal
      // instead of showing the generic banner. Only triggers on a settle
      // attempt so we don't accidentally show it for other 400s.
      if (
        vars.stage === "settled" &&
        err instanceof ApiError &&
        err.status === 400 &&
        err.fieldErrors &&
        Object.keys(err.fieldErrors).length > 0
      ) {
        // Look up the contactId now so onSettled can open the enrol dialog
        // after the modal saves. The board cache was just re-set by the
        // ctx.previous revert two lines above.
        const previousBoard = qc.getQueryData<
          Awaited<ReturnType<typeof dealsApi.pipeline>>
        >(["deals-pipeline"])
        let contactId: string | null = null
        if (previousBoard) {
          for (const s of Object.keys(previousBoard.board) as PipelineStage[]) {
            const found = previousBoard.board[s].find((c) => c.id === vars.id)
            if (found) {
              contactId = found.contactId
              break
            }
          }
        }
        setSettleCompleteDialog({
          dealId: vars.id,
          contactId,
          missingFields: Object.keys(err.fieldErrors),
        })
        return
      }

      const msg = err instanceof Error ? err.message : "Move failed"
      setMoveError(msg)
      // Auto-dismiss after 6s so the banner doesn't stick around forever.
      window.setTimeout(() => setMoveError(null), 6000)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
      qc.invalidateQueries({ queryKey: ["deals"] })
    },
  })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragStart = (event: DragStartEvent) => {
    const card = event.active.data.current as DealPipelineCard | undefined
    if (card) setActiveCard(card)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveCard(null)
    const { active, over } = event
    if (!over) return
    const card = active.data.current as DealPipelineCard | undefined
    if (!card) return
    const targetStage = over.id as PipelineStage
    if (targetStage && card.pipelineStage !== targetStage) {
      move.mutate({ id: card.id, stage: targetStage })
    }
  }

  const actions = (
    <Button asChild>
      <Link to="/deals/new">
        <Plus className="h-4 w-4" />
        New Deal
      </Link>
    </Button>
  )

  return (
    <AppShell
      title="Pipeline"
      subtitle="Drag deals between stages to update their status"
      actions={actions}
    >
      {moveError && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive mb-4 flex items-center justify-between rounded-md border px-3 py-2 text-sm">
          <span>Couldn't move deal: {moveError}</span>
          <button
            type="button"
            onClick={() => setMoveError(null)}
            className="text-destructive/70 hover:text-destructive text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}
      {pipeline.isLoading ? (
        <Card className="grid place-items-center p-16">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </Card>
      ) : pipeline.error ? (
        <Card className="text-destructive p-8 text-center">
          Error loading pipeline: {(pipeline.error as Error).message}
        </Card>
      ) : (
        <>
          {/* Filter bar */}
          <FilterBar
            filters={filters}
            onChange={setFilters}
            brokers={brokers.data?.users ?? []}
          />

          {/* Active-filter summary */}
          <FilterSummary
            filters={filters}
            board={pipeline.data?.board ?? {}}
            onClear={() => setFilters(EMPTY_FILTERS)}
          />

          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4 overflow-x-auto pb-4">
              {pipeline.data?.stages.map((stage) => (
                <PipelineColumn
                  key={stage}
                  stage={stage}
                  cards={applyFilters(pipeline.data.board[stage] ?? [], filters)}
                />
              ))}
            </div>
          {/* dropAnimation=null kills the default "fly back to origin" animation
              that made cards look like they bounced back before landing in the
              new column. The optimistic update in the mutation moves the card
              to the new column immediately. */}
            <DragOverlay dropAnimation={NO_DROP_ANIMATION}>
              {activeCard ? <DealCard card={activeCard} isDragging /> : null}
            </DragOverlay>
          </DndContext>
        </>
      )}

      {/* On-settled enrol dialog. Opens automatically after a drop that
          transitioned a deal into settled (backend response returns
          transitionedIntoSettled + settledEnrolAutomationIds). */}
      {settledDialog && (
        <SettledEnrolDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setSettledDialog(null)
          }}
          dealId={settledDialog.dealId}
          contactId={settledDialog.contactId}
          automationIds={settledDialog.automationIds}
        />
      )}

      {/* Settle-completion dialog. Opens when a drop on Settled is blocked
          by missing required fields (lender / NAF / term / rate). Broker
          fills the missing fields inline; on save we PATCH + re-fire the
          move. On cancel the revert has already been applied via the
          move mutation onError, so we just close. */}
      {settleCompleteDialog && (
        <SettleCompleteDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setSettleCompleteDialog(null)
          }}
          dealId={settleCompleteDialog.dealId}
          missingFields={settleCompleteDialog.missingFields}
          onCancel={() => {
            // Card already snapped back in move.onError. Nothing extra to do.
          }}
          onSettled={(result) => {
            // Same post-settle behaviour as a normal drag-to-settled:
            // if the move transitioned into settled and returned enrol
            // automation ids, open the enrol dialog on top so brokers
            // get the same nurture-signup prompt they'd get from a
            // clean drop.
            if (
              result.transitionedIntoSettled &&
              result.settledEnrolAutomationIds &&
              result.settledEnrolAutomationIds.length > 0 &&
              settleCompleteDialog.contactId
            ) {
              setSettledDialog({
                dealId: settleCompleteDialog.dealId,
                contactId: settleCompleteDialog.contactId,
                automationIds: result.settledEnrolAutomationIds,
              })
            }
          }}
        />
      )}
    </AppShell>
  )
}

function PipelineColumn({ stage, cards }: { stage: PipelineStage; cards: DealPipelineCard[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage })
  const total = cards.reduce((sum, c) => sum + (c.naf ?? c.loanAmount ?? 0), 0)

  return (
    <div className="flex w-72 flex-shrink-0 flex-col">
      <div className="mb-2 flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <div className={cn("h-2 w-2 rounded-full", STAGE_COLOR[stage])} />
          <span className="text-foreground text-sm font-semibold">{STAGE_LABEL[stage]}</span>
          <Badge variant="muted" className="text-xs">
            {cards.length}
          </Badge>
        </div>
      </div>
      <div className="text-muted-foreground mb-2 px-1 text-xs">{formatCurrency(total)}</div>

      <div
        ref={setNodeRef}
        className={cn(
          "min-h-[200px] flex-1 rounded-lg border-2 border-dashed p-2 transition-colors",
          isOver ? "border-rc-blue-500 bg-rc-blue-50/40" : "bg-rc-grey-100/50 border-transparent"
        )}
      >
        <div className="space-y-2">
          {cards.map((card) => (
            <DraggableCard key={card.id} card={card} />
          ))}
          {cards.length === 0 && (
            <div className="text-muted-foreground/60 grid h-full min-h-[100px] place-items-center text-xs">
              Drop deals here
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// dnd-kit doesn't ship a default "skip drop animation" preset, so define one
// that runs for 0ms. Combined with hiding the source node while dragging,
// this makes the drop feel instant.
const NO_DROP_ANIMATION: DropAnimation = {
  duration: 0,
  easing: "linear",
}

function DraggableCard({ card }: { card: DealPipelineCard }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    data: card,
  })

  // While dragging, don't apply the transform to the source node — the
  // DragOverlay renders a floating copy that follows the cursor instead.
  // We also hide the source node completely (visibility:hidden, not
  // display:none, so the column keeps its layout height). This means when
  // the drop happens and the optimistic update swaps the card into the new
  // column, there's no ghost card in the old column for the overlay to
  // animate back to — no bounce-back.
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        visibility: isDragging ? "hidden" : "visible",
      }}
    >
      <DealCard card={card} />
    </div>
  )
}

function DealCard({ card, isDragging }: { card: DealPipelineCard; isDragging?: boolean }) {
  const personName = `${card.contactFirstName ?? ""} ${card.contactLastName ?? ""}`.trim()
  const business = card.contactBusinessName
  // Amount preference: amountRequired (Stage 1 enquiry) → naf (post-approval) → loanAmount (legacy)
  const amount = card.amountRequired ?? card.naf ?? card.loanAmount
  const lenderName = card.lenderName ?? card.lender

  return (
    <Card
      className={cn(
        "cursor-grab p-3 shadow-sm transition-shadow hover:shadow-md",
        isDragging && "ring-rc-blue-500 shadow-lg ring-2"
      )}
    >
      {/* Header: business name on top (bold), person on line below */}
      <Link
        to={`/contacts/${card.contactId}`}
        state={{ from: "pipeline" }}
        className="block"
        onPointerDown={(e) => e.stopPropagation()}
        title="Open contact"
      >
        <div className="text-foreground hover:text-rc-blue-600 truncate text-sm font-semibold">
          {business || personName || "Unnamed contact"}
        </div>
        {business && personName && personName !== business && (
          <div className="text-muted-foreground truncate text-xs">{personName}</div>
        )}
      </Link>

      {/* Loan amount + asset type on one row */}
      <div className="mt-2 flex items-center justify-between gap-2">
        {amount !== null && amount !== undefined ? (
          <span className="text-foreground inline-flex items-center gap-0.5 text-sm font-medium">
            <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
            {formatCurrency(amount)}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">no amount</span>
        )}
        {card.financeCategory && (
          <Badge variant="muted" className="text-[10px] font-normal">
            {card.financeCategory}
          </Badge>
        )}
      </div>

      {/* Lender (subtle, only if picked) */}
      {lenderName && (
        <div className="mt-2 flex items-center gap-1.5">
          <LenderLogo name={lenderName} logoUrl={card.lenderLogoUrl} size="sm" />
          <span className="text-muted-foreground truncate text-[11px]">{lenderName}</span>
        </div>
      )}

      {/* Broker + last-updated footer */}
      <div className="border-border text-muted-foreground mt-2 flex items-center justify-between border-t pt-2 text-[11px]">
        <span className="flex items-center gap-1 truncate">
          <User className="h-3 w-3" />
          {card.ownerName ?? "Unassigned"}
        </span>
        <span>{timeAgo(card.updatedAt)}</span>
      </div>

      {card.priority === "high" && (
        <Badge variant="destructive" className="mt-2 text-[10px]">
          High
        </Badge>
      )}
    </Card>
  )
}

// ============================================================
// Filter helpers + FilterBar + FilterSummary
// ============================================================

function applyFilters(cards: DealPipelineCard[], f: PipelineFilters): DealPipelineCard[] {
  let out = cards
  if (f.q) {
    const q = f.q.toLowerCase()
    out = out.filter((c) => {
      const contactName = `${c.contactFirstName ?? ""} ${c.contactLastName ?? ""}`.trim()
      return (
        (c.dealName ?? "").toLowerCase().includes(q) ||
        contactName.toLowerCase().includes(q) ||
        (c.contactBusinessName ?? "").toLowerCase().includes(q) ||
        (c.lender ?? "").toLowerCase().includes(q) ||
        (c.lenderName ?? "").toLowerCase().includes(q)
      )
    })
  }
  if (f.ownerId) {
    out = out.filter((c) => c.ownerId === f.ownerId)
  }
  if (f.financeType) {
    out = out.filter((c) => c.financeType === f.financeType)
  }
  if (f.priority) {
    out = out.filter((c) => c.priority === f.priority)
  }
  return out
}

type Broker = { id: string; fullName: string }

function FilterBar({
  filters,
  onChange,
  brokers,
}: {
  filters: PipelineFilters
  onChange: (next: PipelineFilters) => void
  brokers: Broker[]
}) {
  const set = <K extends keyof PipelineFilters>(k: K, v: PipelineFilters[K]) =>
    onChange({ ...filters, [k]: v })

  return (
    <Card className="mb-4 p-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search deal, contact, lender…"
            value={filters.q}
            onChange={(e) => set("q", e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="text-muted-foreground h-4 w-4" />
          <select
            className="border-input bg-background h-10 rounded-md border px-3 text-sm"
            value={filters.ownerId}
            onChange={(e) => set("ownerId", e.target.value)}
          >
            <option value="">All brokers</option>
            {brokers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
              </option>
            ))}
          </select>
          <select
            className="border-input bg-background h-10 rounded-md border px-3 text-sm"
            value={filters.financeType}
            onChange={(e) => set("financeType", e.target.value as FinanceType | "")}
          >
            <option value="">All products</option>
            <option value="consumer">Consumer</option>
            <option value="commercial">Commercial</option>
          </select>
          <select
            className="border-input bg-background h-10 rounded-md border px-3 text-sm"
            value={filters.priority}
            onChange={(e) => set("priority", e.target.value as Priority | "")}
          >
            <option value="">All priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>
    </Card>
  )
}

function FilterSummary({
  filters,
  board,
  onClear,
}: {
  filters: PipelineFilters
  board: Record<string, DealPipelineCard[]>
  onClear: () => void
}) {
  const anyActive =
    filters.q || filters.ownerId || filters.financeType || filters.priority

  const { totalMatching, totalOverall, totalValue } = useMemo(() => {
    let totalMatching = 0
    let totalOverall = 0
    let totalValue = 0
    for (const stage of Object.keys(board)) {
      const cards = board[stage] ?? []
      totalOverall += cards.length
      const filtered = applyFilters(cards, filters)
      totalMatching += filtered.length
      totalValue += filtered.reduce(
        (sum, c) => sum + (c.naf ?? c.loanAmount ?? 0),
        0
      )
    }
    return { totalMatching, totalOverall, totalValue }
  }, [board, filters])

  if (!anyActive) return null

  return (
    <div className="mb-3 flex items-center justify-between rounded-md border border-rc-blue-200 bg-rc-blue-50/40 px-3 py-2 text-xs text-rc-blue-900">
      <div>
        <span className="font-semibold">
          {totalMatching} of {totalOverall}
        </span>{" "}
        deals matching · pipeline value{" "}
        <span className="font-semibold">{formatCurrency(totalValue)}</span>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="text-rc-blue-700 hover:text-rc-blue-900 inline-flex items-center gap-1"
      >
        <X className="h-3 w-3" />
        Clear filters
      </button>
    </div>
  )
}
